import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import config from "../config"
import { usePlaybackRate } from "../hooks/audio"
import { useBooleanState } from "../hooks/use-boolean-state"
import { audioPlayer } from "../lib/audio-player"
import Select from "./select"
import Text from "./text"
import Touchable from "./touchable"

const PLAYBACK_RATE_BUTTON_HIT_SLOP = { top: 8, right: 8, bottom: 8, left: 8 }

const PLAYBACK_RATE_SELECT_MAX_WIDTH_PX = 280

const playbackRateToString = (rate: number): string =>
  rate.toFixed(config.PLAYBACK_RATE_DECIMALS).replace(/(\d)0+/, "$1") + "x"

const playbackRateOptions = config.PLAYBACK_RATES.map((rate) => ({
  label: playbackRateToString(rate),
  value: rate.toFixed(config.PLAYBACK_RATE_DECIMALS),
}))

export function PlaybackRateSelect(): React.JSX.Element {
  const playbackRate = usePlaybackRate()

  const [rateSelectOpen, openRateSelect, closeRateSelect] =
    useBooleanState(false)

  const { t } = useTranslation()

  const onSelectRateOption = useCallback(
    (value: string) => {
      audioPlayer.setPlaybackRate(parseFloat(value))
      closeRateSelect()
    },
    [closeRateSelect],
  )

  return (
    <>
      <Touchable
        id="audio-player-controls-playback-rate"
        onPress={openRateSelect}
        feedback="opacity"
        hitSlop={PLAYBACK_RATE_BUTTON_HIT_SLOP}
      >
        <Text textStyle="headingSmall">
          {playbackRateToString(playbackRate)}
        </Text>
      </Touchable>
      <Select
        id="audio-player-controls-playback-rate-selector"
        label={t("Playback Rate")}
        open={rateSelectOpen}
        onClose={closeRateSelect}
        value={playbackRate.toFixed(config.PLAYBACK_RATE_DECIMALS)}
        onChange={onSelectRateOption}
        options={playbackRateOptions}
        maxWidth={PLAYBACK_RATE_SELECT_MAX_WIDTH_PX}
      />
    </>
  )
}
